{
  "title": "Data tools",
  "subtitle": "Bring data in from Excel, take it out, and keep it safe with backups.",
  "nothing": "Your role gives no access to imports or exports.",
  "import": {
    "title": "Import products from Excel",
    "subtitle": "Excel (.xlsx) or CSV, in English or French. Nothing is created before you confirm.",
    "template": "Download the template",
    "kind": "What to import",
    "kinds": {
      "products": "Products",
      "openingStock": "Opening stock",
      "categories": "Categories",
      "units": "Units",
      "brands": "Brands"
    }
  },
  "steps": {
    "choose": "Choose file",
    "chooseHint": "Excel or CSV, up to 10 MB",
    "check": "Check",
    "checkHint": "Every row is checked",
    "checking": "Checking…",
    "checkingLong": "Checking {{file}}: every row, every code, every number…",
    "checkFailed": "The file could not be read",
    "problems_one": "We found {{formatted}} problem",
    "problems_other": "We found {{formatted}} problems",
    "noProblem": "No problem found",
    "import": "Import",
    "importHint": "Products are created",
    "importing": "Importing…",
    "done": "Done"
  },
  "choose": {
    "drop": "Drop your file here or click to choose",
    "limits": "Excel (.xlsx) or CSV · up to 10 MB and 50,000 rows",
    "another": "Click to choose another file",
    "wrongType": "Use an Excel (.xlsx) or CSV file",
    "tooLarge": "The file must be 10 MB or less",
    "createMissing": "Create missing categories and brands",
    "createMissingHint": "Otherwise rows with an unknown category or brand are reported. Units must exist: they set the decimals.",
    "updateExisting": "Update products that already exist (same SKU)",
    "updateExistingHint": "Empty cells keep the current value. Without this option, existing SKUs are reported as problems.",
    "check": "Check the file"
  },
  "check": {
    "file": "File",
    "rowsRead": "Rows read",
    "ready": "Ready to import",
    "needFixing": "Rows to fix",
    "updates": "{{create}} new products and {{update}} updates of existing products.",
    "columns_one": "{{count}} column recognised",
    "columns_other": "{{count}} columns recognised",
    "ignoredCount_one": "· {{count}} column ignored",
    "ignoredCount_other": "· {{count}} columns ignored",
    "ignored": "Not imported (no matching field): {{headers}}.",
    "row": "Row",
    "product": "Product",
    "column": "Column",
    "problem": "Problem and how to fix it",
    "empty": "(empty)",
    "allGood": "Every row can be imported.",
    "showing": "Showing {{shown}} of {{total}} problems",
    "showAll": "Show all",
    "showLess": "Show less",
    "downloadErrors": "Download the error list",
    "uploadFixed": "Upload a fixed file",
    "otherFile": "Choose another file",
    "importGood_one": "Import the {{formatted}} good row",
    "importGood_other": "Import the {{formatted}} good rows",
    "importAll_one": "Import {{formatted}} product",
    "importAll_other": "Import {{formatted}} products",
    "name": "Name"
  },
  "outcome": {
    "importing": "Importing: {{processed}} of {{total}} rows",
    "importingHint": "You can leave this page: the import goes on, and you will find the result here.",
    "failed": "The import did not go through",
    "missingColumns": "These required columns are missing: {{columns}}. Use the template, or name your columns name, category and unit.",
    "tryAgain": "Try another file",
    "done": "Import {{number}} done",
    "created_one": "{{formatted}} product created",
    "created_other": "{{formatted}} products created",
    "updated_one": "{{formatted}} product updated",
    "updated_other": "{{formatted}} products updated",
    "categories_one": "{{count}} category created",
    "categories_other": "{{count}} categories created",
    "brands_one": "{{count}} brand created",
    "brands_other": "{{count}} brands created",
    "another": "Import another file",
    "seeProducts": "See the products"
  },
  "other": {
    "title": "Other imports and exports",
    "exportTitle": "Any list to Excel",
    "exportText": "Products, locations, users, units, categories, brands…",
    "export": "Export",
    "templatesTitle": "Excel template",
    "templatesText": "The product file with the right columns, your lists and help",
    "download": "Download",
    "openingTitle": "Opening stock",
    "openingText": "Quantities and costs per location when you start",
    "listsTitle": "Lists",
    "listsText": "Categories, units and brands from a file",
    "import": "Import"
  },
  "export": {
    "title": "Export a list to Excel",
    "hint": "In your language, with costs only if you can see them.",
    "download": "Download",
    "lists": {
      "products": "Products",
      "locations": "Locations",
      "users": "Users",
      "units": "Units of measure",
      "categories": "Categories",
      "brands": "Brands",
      "adjustment-reasons": "Adjustment reasons",
      "operations": "Stock in & out",
      "stock-levels": "Stock levels",
      "movements": "Stock movements",
      "activity": "Activity log",
      "transfers": "Transfers",
      "customers": "Customers"
    },
    "details": {
      "products": "Active products with stock, limits and prices",
      "locations": "Sites and their sub-locations",
      "users": "People, roles and where they work",
      "units": "With their decimals and use",
      "categories": "The full tree, one path per line",
      "brands": "With their use",
      "adjustment-reasons": "With their direction",
      "operations": "Every operation with its status and value",
      "stock-levels": "Quantities per location, total and value",
      "movements": "The ledger: every quantity change",
      "activity": "Every recorded action",
      "transfers": "Every transfer with its places, dates and status",
      "customers": "Contacts, default address, tags and consent"
    }
  },
  "backups": {
    "title": "Backups",
    "now": "Back up now",
    "started": "Backup started: it is written in the background",
    "automatic": "Automatic backup every day at {{time}}.",
    "kept_one": "Kept for {{count}} day.",
    "kept_other": "Kept for {{count}} days.",
    "last": "Last one: {{when}}, {{size}}.",
    "noneYet": "The first one runs tonight.",
    "empty": "No backup yet.",
    "auto": "automatic",
    "manualBy": "manual by {{name}}",
    "done": "Done",
    "failed": "Failed",
    "running": "Running",
    "download": "Download",
    "restoreTitle": "Restore from a backup",
    "restoreText": "Brings back all the data and files of a backup. For the Administrator only, confirmed with the company name.",
    "safety": "safety copy before a restore"
  },
  "opening": {
    "title": "Import opening stock",
    "subtitle": "Quantities and costs per location when you start. The import prepares a draft: you check it and validate it.",
    "hint": "Each row names a product (SKU or barcode), a location (code or name), the quantity and the unit cost. Rows without a quantity are skipped.",
    "create_one": "Create the opening stock draft ({{formatted}} line)",
    "create_other": "Create the opening stock draft ({{formatted}} lines)",
    "done": "Draft {{number}} created",
    "doneText_one": "{{formatted}} line is ready. Nothing has moved yet: review it and validate it.",
    "doneText_other": "{{formatted}} lines are ready. Nothing has moved yet: review them and validate.",
    "review": "Review and validate"
  },
  "restore": {
    "action": "Restore",
    "title": "Restore this backup",
    "warningTitle": "The data goes back to {{when}}",
    "replaces": "Everything entered since is replaced: products, stock, orders, payments, users, settings and files.",
    "safety": "A safety copy of the current data is made first: you can come back to it from the list.",
    "closed": "The app is closed for everyone while it runs.",
    "signedOut": "Everyone signs in again afterwards, with the accounts and passwords of the backup.",
    "archive": "Made with version {{version}} of the database \"{{database}}\".",
    "type": "Type the company name to confirm: {{name}}",
    "confirm": "Restore",
    "fromFile": "Restore from a file",
    "tooLarge": "The file is larger than {{max}} GB.",
    "start": "Restore",
    "discard": "Remove the file",
    "statuses": {
      "ready": "Waiting to be confirmed",
      "running": "Running",
      "done": "Restored",
      "failed": "Failed"
    },
    "safetyCopy": "safety copy: {{name}}",
    "stockOk": "stock reconciliation OK",
    "stockDiff_one": "{{count}} stock difference found",
    "stockDiff_other": "{{count}} stock differences found",
    "errors": {
      "manifest": "This file is not a Light BM backup.",
      "format": "This backup was made by an older version and cannot be restored.",
      "unreadable": "The file is damaged or cut: it cannot be read to the end.",
      "incomplete": "The backup is incomplete (cut before its end).",
      "database": "The data of the backup is not in order.",
      "files": "The files of the backup are not in order.",
      "missing": "The backup file is no longer in the storage.",
      "safety": "The safety copy could not be made: nothing was changed.",
      "unavailable": "The background service was not available: nothing was changed.",
      "unexpected": "An unexpected error stopped it (see the server logs)."
    },
    "partial": "The data may be partly restored: restore the safety copy."
  },
  "lists": {
    "fromFile": "Import from a file",
    "skipped_one": "{{formatted}} row found in the list meanwhile, left as it is",
    "skipped_other": "{{formatted}} rows found in the list meanwhile, left as they are",
    "categories": {
      "title": "Import categories",
      "subtitle": "Excel (.xlsx) or CSV. Categories already in the list are left as they are.",
      "hint": "Each row names a category, with the path of its parent in \"parent\" or the whole path in the name (Paint > Interior). Missing parents are created on the way.",
      "create_one": "Create {{formatted}} category",
      "create_other": "Create {{formatted}} categories",
      "created_one": "{{formatted}} category created",
      "created_other": "{{formatted}} categories created",
      "see": "See the categories"
    },
    "units": {
      "title": "Import units",
      "subtitle": "Excel (.xlsx) or CSV. Units already in the list (by name or symbol) are left as they are.",
      "hint": "Each row names a unit, its symbol and how many decimals its quantities may have.",
      "create_one": "Create {{formatted}} unit",
      "create_other": "Create {{formatted}} units",
      "created_one": "{{formatted}} unit created",
      "created_other": "{{formatted}} units created",
      "see": "See the units"
    },
    "brands": {
      "title": "Import brands",
      "subtitle": "Excel (.xlsx) or CSV. Brands already in the list are left as they are.",
      "hint": "One brand name per row.",
      "create_one": "Create {{formatted}} brand",
      "create_other": "Create {{formatted}} brands",
      "created_one": "{{formatted}} brand created",
      "created_other": "{{formatted}} brands created",
      "see": "See the brands"
    }
  }
}
